import { useLayoutEffect } from "react";
import { useLocation, useNavigationType } from "react-router";

const scrollPositions = [];
const MAX_RESTORE_ATTEMPTS = 60;
const MAX_SCROLL_POSITIONS = 20;

function useMainContentScrollRestoration(containerId = "main-content") {
	const location = useLocation();
	const navigationType = useNavigationType();
	const locationId = `${location.pathname}${location.search}${location.hash}`;

	useLayoutEffect(() => {
		const scrollContainer = document.getElementById(containerId);
		if (!scrollContainer) {
			return undefined;
		}

		let frameId = null;
		let restoreAttempts = 0;
		let resizeObserver = null;

		const saveScrollPosition = () => {
			const existingIndex = scrollPositions.findIndex(
				(pos) => pos.locationId === locationId
			);
			if (
				existingIndex === -1 &&
				scrollPositions.length >= MAX_SCROLL_POSITIONS
			) {
				scrollPositions.shift();
			} else if (existingIndex !== -1) {
				scrollPositions.splice(existingIndex, 1);
			}

			scrollPositions.push({
				locationId,
				top: scrollContainer.scrollTop,
				left: scrollContainer.scrollLeft,
			});
		};

		const handleScroll = () => {
			if (frameId !== null) {
				cancelAnimationFrame(frameId);
			}
			frameId = requestAnimationFrame(() => {
				saveScrollPosition();
				frameId = null;
			});
		};

		const applyScrollPosition = (position) => {
			scrollContainer.scrollTo({
				top: position?.top ?? 0,
				left: position?.left ?? 0,
				behavior: "auto",
			});
		};

		const canFullyRestoreScrollPosition = (position) => {
			if (!position) {
				return true;
			}

			const maxScrollTop = Math.max(
				scrollContainer.scrollHeight - scrollContainer.clientHeight,
				0
			);
			const maxScrollLeft = Math.max(
				scrollContainer.scrollWidth - scrollContainer.clientWidth,
				0
			);

			return (
				maxScrollTop >= position.top && maxScrollLeft >= position.left
			);
		};

		scrollContainer.addEventListener("scroll", handleScroll, {
			passive: true,
		});

		const savedPosition =
			navigationType === "POP"
				? scrollPositions.find((pos) => pos.locationId === locationId)
				: null;

		applyScrollPosition(savedPosition);

		if (navigationType === "POP" && savedPosition) {
			const restoreScrollPosition = () => {
				applyScrollPosition(savedPosition);
				if (canFullyRestoreScrollPosition(savedPosition)) {
					return;
				}

				if (restoreAttempts >= MAX_RESTORE_ATTEMPTS) {
					return;
				}

				restoreAttempts += 1;
				frameId = requestAnimationFrame(() => {
					frameId = null;
					restoreScrollPosition();
				});
			};

			restoreScrollPosition();

			if (typeof ResizeObserver !== "undefined") {
				resizeObserver = new ResizeObserver(() => {
					restoreScrollPosition();
				});
				resizeObserver.observe(scrollContainer);
			}
		}

		return () => {
			if (frameId !== null) {
				cancelAnimationFrame(frameId);
			}
			if (resizeObserver) {
				resizeObserver.disconnect();
			}
			scrollContainer.removeEventListener("scroll", handleScroll);
		};
	}, [containerId, locationId, navigationType]);
}

export default useMainContentScrollRestoration;
